import { memo } from "react";
import ChoixRenderer from "../ChoixRenderer";
import ChoixImageEleve from "./ChoixImageEleve";
import ChoixZoomButton from "./ChoixZoomButton";
import { useQuizTheme, answerTheme } from "./quizTheme";
import { playTick } from "../../../utils/xpSound";
import { etatTuile, MarqueCorrection, TUILE_NEUTRE } from "./corrige";

/**
 * Valeur discriminante d'un choix QCM pour comparaison/sélection.
 * Format contenu.choix : string directe ou objet {type, config}
 * - string → la string elle-même
 * - objet image → config.imageUrl
 * - objet autre → JSON.stringify
 */
const getChoixValue = (choix) => {
    if (typeof choix === 'string') return choix;
    // Image : imageUrl (prof) sinon JSON de l'objet (exerciseur = imageData) → valeur
    // UNIQUE par choix, cohérente avec resolveBonneReponse (qui sérialise aussi en JSON).
    // Sans ça, deux images sans imageUrl renvoyaient '' → collision → tous sélectionnés.
    if (choix?.type === 'image') return choix.config?.imageUrl || JSON.stringify(choix);
    if (typeof choix === 'object') return JSON.stringify(choix);
    return '';
};

/**
 * Composant interactif QCM côté élève — choix unique (radio buttons).
 * Appelle onAnswer(valeur, imageUrl) quand l'élève sélectionne un choix.
 *
 * Design adaptatif via useQuizTheme() : 'clair' (carte blanche, mockup) pendant
 * la passation, 'sombre' (défaut) ailleurs (exerciseur). Aucune modif de logique.
 *
 * @param {Array} choix - Les choix ["texte", {type:'image', config:{imageUrl}}, ...]
 * @param {string} selectedValue - La réponse déjà sélectionnée (ou null)
 * @param {Function} onAnswer - Callback (valeur, imageUrl) quand l'élève clique
 * @param {Function} onComplete - Callback (bool) pour signaler que la réponse est complète
 * @param {string} radioName - Nom unique pour les radio buttons (éviter les conflits)
 * @param {?{bonneReponse:string, reponseEleve:string}} [corrige=null] - Correction
 *   SUR PLACE (lot 1, 2026-09-21) : chaque tuile prend l'un des trois états de
 *   answerTheme (juste / faux / attendu), le widget passe en lecture seule, la
 *   consigne disparaît. Absente : le rendu d'avant, au bit près.
 */
const QCMEleve = memo(function QCMEleve({ choix, selectedValue, onAnswer, onComplete, radioName, corrige = null }) {
    const clair = useQuizTheme() === 'clair';
    const t = answerTheme(clair);

    if (!choix || choix.length === 0) return null;

    const hasElement = choix.some(c => typeof c === 'object' && c !== null);

    return (
        <>
            {/* Instructions : pas en correction, il n'y a plus rien à cliquer */}
            {corrige ? null : (
                <p className={`text-xs sm:text-sm font-bold flex items-center gap-2 ${clair ? 'text-slate-500' : 'text-slate-300'}`}>
                    <span>👇</span> Clique sur la bonne réponse puis clique sur suivant !
                </p>
            )}

            {/* Réponses — texte pleine largeur (1 col) ; images en tuiles 2×2 (Duolingo) */}
            <div className={`grid gap-3 mt-3 sm:mt-4 ${hasElement ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {choix.map((item, index) => {
                    const value = getChoixValue(item);
                    const isSelected = selectedValue === value;
                    const isObjectChoix = typeof item === 'object' && item !== null;
                    const isImage = isObjectChoix && item?.type === 'image';
                    const imageUrl = isImage ? (item.config?.imageUrl || '') : null;

                    // En correction, l'état de la tuile remplace la sélection : la
                    // nuance vient d'answerTheme, jamais d'ici.
                    const etat = etatTuile(corrige, value);
                    const stateClass = corrige
                        ? (etat ? t[etat] : (clair ? TUILE_NEUTRE.clair : TUILE_NEUTRE.sombre))
                        : (clair
                            ? (isSelected
                                ? 'quiz-tile-pop border-[#3B82F6] border-b-[5px] border-b-[#2563EB] bg-blue-50 active:translate-y-[3px] active:border-b-2'
                                : 'border-slate-200 border-b-[5px] border-b-slate-300 bg-white hover:border-blue-300 active:translate-y-[3px] active:border-b-2')
                            : (isSelected
                                ? 'border-[#29B6E8] bg-[#29B6E8]/20 shadow-lg shadow-[#29B6E8]/20'
                                : 'border-white/10 bg-white/5 hover:border-[#29B6E8]/60 hover:bg-white/10'));
                    const textClass = corrige
                        ? (etat ? 'text-inherit' : (clair ? 'text-slate-800' : 'text-slate-100'))
                        : (clair
                            ? (isSelected ? 'text-[#2563EB]' : 'text-slate-800')
                            : 'text-slate-100');

                    return (
                        <div key={index} className="relative flex">
                        <label
                            className={`relative flex w-full items-center justify-center text-center p-3 sm:p-4 border-2 rounded-2xl transition-all duration-200 ${corrige ? 'cursor-default' : 'cursor-pointer hover:scale-[1.01]'} ${isImage ? 'min-h-[7rem]' : ''} ${stateClass}`}
                        >
                            {etat ? <MarqueCorrection etat={etat} /> : null}
                            {isImage ? (
                                /* Choix image : tuile « Duolingo » — image contenue + placeholder si indisponible (tuile cliquable) */
                                <div className="pointer-events-none w-full flex items-center justify-center">
                                    <ChoixImageEleve
                                        src={imageUrl || item.config?.imageData || ''}
                                        alt={`Réponse ${index + 1}`}
                                    />
                                </div>
                            ) : isObjectChoix ? (
                                /* Élément visuel (droite / expression / geogebra) : pointer-events-none →
                                   la tuile sélectionne, le zoom interne de l'élément ne se déclenche plus (loupe dédiée) */
                                <div className="pointer-events-none w-full flex items-center justify-center">
                                    <ChoixRenderer
                                        choix={item}
                                        light={clair}
                                        className={`text-lg sm:text-xl font-semibold ${textClass}`}
                                    />
                                </div>
                            ) : (
                                /* Texte (string) via ChoixRenderer (rendu KaTeX des $...$) */
                                <ChoixRenderer
                                    choix={item}
                                    light={clair}
                                    className={`text-lg sm:text-xl font-semibold ${textClass}`}
                                />
                            )}

                            {/* Input réel masqué (le label entier est la cible cliquable) */}
                            <input
                                type="radio"
                                name={radioName}
                                value={value}
                                checked={isSelected}
                                disabled={Boolean(corrige)}
                                onChange={() => { playTick(); onAnswer(value, imageUrl); onComplete(true); }}
                                className="sr-only"
                            />
                        </label>

                        {/* Loupe HORS du <label> → un clic dessus ne peut pas déclencher la sélection */}
                        {isObjectChoix && <ChoixZoomButton choix={item} light={clair} />}
                        </div>
                    );
                })}
            </div>
        </>
    );
});

export { getChoixValue };
export default QCMEleve;
